import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Same-origin: nginx (prod) and the Vite dev proxy both route /api to the
// backend. Absolute http:// URLs would be blocked as mixed content over HTTPS.
const API_URL = window.location.origin;

export const fetchSuiteExecutions = createAsyncThunk(
	"suiteExecutions/fetchAll",
	async ({ workspaceId = "default" } = {}) => {
		const response = await api.get(`${API_URL}/api/suites/executions`, {
			params: { workspaceId },
		});
		return response.data;
	},
);

export const fetchSuiteExecutionDetail = createAsyncThunk(
	"suiteExecutions/fetchDetail",
	async (suiteExecutionId) => {
		const response = await api.get(
			`${API_URL}/api/suites/executions/${suiteExecutionId}`,
		);
		return response.data;
	},
);

export const fetchChildExecutions = createAsyncThunk(
	"suiteExecutions/fetchChildren",
	async (suiteExecutionId) => {
		const response = await api.get(
			`${API_URL}/api/suites/executions/${suiteExecutionId}/tests`,
		);
		return response.data;
	},
);

// Stop a running suite run and all of its unfinished tests
export const cancelSuiteExecution = createAsyncThunk(
	"suiteExecutions/cancel",
	async (suiteExecutionId, { rejectWithValue }) => {
		try {
			const response = await api.post(
				`${API_URL}/api/suites/executions/${suiteExecutionId}/cancel`,
			);
			return response.data;
		} catch (error) {
			return rejectWithValue({
				status: error.response?.status,
				message: error.message,
			});
		}
	},
);

export const rerunFailedTests = createAsyncThunk(
	"suiteExecutions/rerunFailed",
	async (suiteExecutionId) => {
		const response = await api.post(
			`${API_URL}/api/suites/executions/${suiteExecutionId}/rerun-failed`,
		);
		return response.data;
	},
);

export const fetchSuiteExecutionReport = createAsyncThunk(
	"suiteExecutions/fetchExecutionReport",
	async (suiteExecutionId) => {
		const response = await api.get(
			`${API_URL}/api/suites/executions/${suiteExecutionId}/report`,
		);
		return response.data;
	},
);

export const fetchSuiteReport = createAsyncThunk(
	"suiteExecutions/fetchSuiteReport",
	async (suiteId) => {
		const response = await api.get(
			`${API_URL}/api/suites/${suiteId}/report`,
		);
		return response.data;
	},
);

const suiteExecutionSlice = createSlice({
	name: "suiteExecutions",
	initialState: {
		suiteExecutions: [],
		selectedSuiteExecution: null,
		childExecutions: [],
		executionReport: null,
		suiteReport: null,
		loading: false,
		childrenLoading: false,
		rerunLoading: false,
		reportLoading: false,
		suiteReportLoading: false,
		error: null,
	},
	reducers: {
		selectSuiteExecution: (state, action) => {
			state.selectedSuiteExecution = action.payload;
			state.executionReport = null;
		},
		clearSelectedSuiteExecution: (state) => {
			state.selectedSuiteExecution = null;
			state.childExecutions = [];
			state.executionReport = null;
		},
		clearSuiteReport: (state) => {
			state.suiteReport = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSuiteExecutions.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchSuiteExecutions.fulfilled, (state, action) => {
				state.loading = false;
				state.suiteExecutions = action.payload;
				// Keep selectedSuiteExecution in sync with refreshed data
				if (state.selectedSuiteExecution) {
					const updated = action.payload.find(
						(e) => e.id === state.selectedSuiteExecution.id,
					);
					if (updated) {
						state.selectedSuiteExecution = updated;
					}
				}
			})
			.addCase(fetchSuiteExecutions.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			.addCase(fetchSuiteExecutionDetail.fulfilled, (state, action) => {
				state.selectedSuiteExecution = action.payload;
			})

			.addCase(fetchChildExecutions.pending, (state) => {
				state.childrenLoading = true;
			})
			.addCase(fetchChildExecutions.fulfilled, (state, action) => {
				state.childrenLoading = false;
				state.childExecutions = action.payload;
			})
			.addCase(fetchChildExecutions.rejected, (state) => {
				state.childrenLoading = false;
			})

			.addCase(cancelSuiteExecution.fulfilled, (state, action) => {
				const stopped = action.payload;
				state.suiteExecutions = state.suiteExecutions.map((e) =>
					e.id === stopped.id ? stopped : e,
				);
				if (state.selectedSuiteExecution?.id === stopped.id) {
					state.selectedSuiteExecution = stopped;
				}
			})

			.addCase(rerunFailedTests.pending, (state) => {
				state.rerunLoading = true;
			})
			.addCase(rerunFailedTests.fulfilled, (state, action) => {
				state.rerunLoading = false;
				// Add the new rerun suite execution to the top of the list
				state.suiteExecutions.unshift(action.payload);
				// Select the new rerun execution
				state.selectedSuiteExecution = action.payload;
			})
			.addCase(rerunFailedTests.rejected, (state) => {
				state.rerunLoading = false;
			})

			// The report refreshes while a run progresses: show the spinner only
			// when there's nothing to show yet, not on every refresh.
			.addCase(fetchSuiteExecutionReport.pending, (state) => {
				if (!state.executionReport) state.reportLoading = true;
			})
			.addCase(fetchSuiteExecutionReport.fulfilled, (state, action) => {
				state.reportLoading = false;
				// Ignore a late answer for a run that is no longer selected
				if (action.meta.arg === state.selectedSuiteExecution?.id) {
					state.executionReport = action.payload;
				}
			})
			.addCase(fetchSuiteExecutionReport.rejected, (state) => {
				state.reportLoading = false;
			})

			.addCase(fetchSuiteReport.pending, (state) => {
				state.suiteReportLoading = true;
			})
			.addCase(fetchSuiteReport.fulfilled, (state, action) => {
				state.suiteReportLoading = false;
				state.suiteReport = action.payload;
			})
			.addCase(fetchSuiteReport.rejected, (state) => {
				state.suiteReportLoading = false;
			});
	},
});

export const {
	selectSuiteExecution,
	clearSelectedSuiteExecution,
	clearSuiteReport,
} = suiteExecutionSlice.actions;
export default suiteExecutionSlice.reducer;
